// SuperSideNav — sidebar for the platform super-admin shell.
//
// Standalone from the tenant Sidebar / SidebarContext: super-admins have
// a fixed four-item nav and no module switching, so we don't reuse the
// tenant rail's content-injection mechanism.

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  ShieldCheck,
  Activity,
  ScrollText,
  Scale,
  Layers,
} from 'lucide-react';
import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../utils/cn';
import styles from './SuperSideNav.module.css';

const COLLAPSE_KEY = 'tricivo:super-sidebar:collapsed';

interface SuperNavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  end?: boolean;
}

const ITEMS: SuperNavItem[] = [
  { to: '/super', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/super/companies', label: 'Companies', icon: Building2 },
  { to: '/super/admins', label: 'Admins', icon: ShieldCheck },
  { to: '/super/system', label: 'System', icon: Activity },
  { to: '/super/logs', label: 'Logs', icon: ScrollText },
  { to: '/super/wb-activity', label: 'WB Activity', icon: Scale },
  { to: '/super/product-groups', label: 'Product Groups', icon: Layers },
];

function readPersistedCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function SuperSideNav(): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(readPersistedCollapsed);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(
      '--sidebar-current-width',
      isCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
    );
  }, [isCollapsed]);

  function toggle() {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // localStorage unavailable — silently degrade.
      }
      return next;
    });
  }

  return (
    <nav
      className={cn(styles.sidebar, isCollapsed && styles.collapsed)}
      aria-label="Super admin navigation"
    >
      <ul className={styles.list}>
        {ITEMS.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(styles.link, isActive && styles.linkActive)
              }
              title={isCollapsed ? item.label : undefined}
            >
              <span className={styles.linkInner}>
                <span className={styles.icon}>
                  <item.icon size={18} aria-hidden />
                </span>
                <span className={styles.label}>{item.label}</span>
              </span>
            </NavLink>
          </li>
        ))}
      </ul>

      <div className={styles.spacer} />
      <div className={styles.toggleRow}>
        <button
          type="button"
          className={styles.toggle}
          onClick={toggle}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {isCollapsed ? (
            <ChevronRight size={16} aria-hidden />
          ) : (
            <>
              <ChevronLeft size={16} aria-hidden />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </nav>
  );
}
